import { useState, useRef } from 'react';
import { NodeProps, Node, Handle, Position } from '@xyflow/react';
import { Result1 } from '@mm-shared/types/Result1';
import { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { GRID_CONFIG } from '../gridConfig';
import { FlowTooltip } from './FlowTooltip';
import {
  getResultCircleLayoutForReactFlow,
  getResultCircleColor,
} from '../../utils/resultCircleUtils';

export interface DraggableResultCircleNodeData extends Record<string, unknown> {
  result: Result1;
  objectiveUuid: string;
  rowIndex: number;
  columnIndex: number;
  statusCode: string;
  enableFill?: boolean;
  showStatusCodes?: boolean; // Whether to show M/i indicators
  // Callbacks for mode switching
  onExitDragAndConnectMode?: (resultUuid: string) => void;
  isConnecting?: boolean;
  isDragging?: boolean;
}

export type CustomDraggableResultCircleNode = Node<DraggableResultCircleNodeData>;

/**
 * DraggableResultCircleNode Component
 *
 * Optimized for React Flow drag and drop interactions.
 * Follows the same pattern as ResultNode to ensure compatibility.
 * Single circle per node, designed for reliable drag/drop and dependency creation.
 */
export function DraggableResultCircleNode(props: NodeProps<CustomDraggableResultCircleNode>) {
  const { data } = props;
  const [showTooltip, setShowTooltip] = useState(false);
  const circleRef = useRef<HTMLDivElement>(null);

  // Use single circle layout for reliable React Flow interaction
  const layout = getResultCircleLayoutForReactFlow(1);
  const circleColor = getResultCircleColor(data.result, data.statusCode, data.enableFill ?? true);
  const nodePadding = 4; // Same padding as ResultNode

  const handleClick = () => {
    // If not dragging/connecting, exit drag and connect mode
    if (!data.isConnecting && !data.isDragging) {
      data.onExitDragAndConnectMode?.(data.result.uuid);
    }
  };

  return (
    <div
      className="group relative"
      style={{
        cursor: 'grab',
        width: `${GRID_CONFIG.cellWidth - 2 * nodePadding}px`, // Match ResultNode container size
        height: `${GRID_CONFIG.cellHeight - 2 * nodePadding}px`, // Match ResultNode container size
        position: 'relative',
        left: `${nodePadding}px`,
        top: `${nodePadding}px`,
      }}
      onMouseDown={(e) => {
        // Change cursor to grabbing when dragging the node (not handles)
        if (e.target === e.currentTarget) {
          e.currentTarget.style.cursor = 'grabbing';
        }
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.cursor = 'grab';
      }}
      onClick={handleClick}
      onMouseEnter={() => {
        setShowTooltip(true);
      }}
      onMouseLeave={() => {
        setShowTooltip(false);
      }}
    >
      {/* Left handle at 9:00 position - for outgoing connections */}
      <Handle
        type="source"
        position={Position.Left}
        style={{
          left: '0px',
          top: '50%',
          width: '8px',
          height: '8px',
          background: '#6b7280',
          border: '2px solid #374151',
          borderRadius: '50%',
          opacity: 1,
          zIndex: 10,
          transform: 'translateY(-50%)',
          pointerEvents: 'auto', // Allow handle interactions
          cursor: 'crosshair', // Override container cursor for handles
        }}
      />

      {/* Right handle at 3:00 position - for incoming connections */}
      <Handle
        type="target"
        position={Position.Right}
        style={{
          left: `${layout.radius * 2 - 6}px`, // Right edge of centered circle (4px = half handle width)
          top: '50%',
          width: '8px',
          height: '8px',
          background: '#6b7280',
          border: '2px solid #374151',
          borderRadius: '50%',
          opacity: 0, // Always hidden to dissuade users from grabbing this one. Otherwise the arrow appears "backward" and it's confusing to figure out why.
          zIndex: 10,
          transform: 'translateY(-50%)',
          pointerEvents: 'auto', // Allow handle interactions even when hidden
          cursor: 'crosshair', // Override container cursor for handles
        }}
      />

      {/* The actual circle representing the result */}
      <div
        ref={circleRef}
        className="rounded-full border-2 border-white flex items-center justify-center"
        style={{
          width: `${layout.radius * 2}px`,
          height: `${layout.radius * 2}px`,
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)', // Center within container
          backgroundColor: circleColor,
          zIndex: showTooltip ? GRID_CONFIG.zIndex.nodes + 1 : GRID_CONFIG.zIndex.nodes, // Use centralized z-index, boost on hover
        }}
      >
        {data.showStatusCodes && (
          <div className="text-xs font-semibold text-white">
            {data.result.isKeyResult ? 'M' : 'i'}
          </div>
        )}
      </div>

      {/* Tooltip using FlowTooltip component for proper layering and viewport handling */}
      <FlowTooltip
        show={showTooltip}
        targetRef={circleRef}
        content={
          <div>
            <div className="font-semibold">
              {data.result.isKeyResult ? 'Milestone' : 'Inchstone'}
            </div>
            <div className="max-w-xs">{data.result.text}</div>
            <div className="text-gray-300">
              {data.result.delivered ? 'Delivered' : 'Pending'}
              {data.result.deadline &&
                ` • Due: ${new Date(data.result.deadline).toLocaleDateString()}`}
            </div>
            <div className="text-xs text-gray-400 mt-1">
              Click to exit drag mode • Press Escape to exit • Drag to move • Use handles to connect
            </div>
          </div>
        }
      />
    </div>
  );
}

// Register custom node type
export const draggableCircleNodeTypes = {
  draggableResultCircleNode: DraggableResultCircleNode,
};
